import type { ActivityMessage } from "@/api/models"

const MAX_ACTIVITY_COUNT = 1_000_000
/** Citation keys one grounded activity may carry; the backend caps at the same. */
export const MAX_FILTH_CITATION_KEYS = 100
/** Citation keys a whole turn may carry, over all of its library searches. */
export const MAX_TURN_FILTH_CITATION_KEYS = 4 * MAX_FILTH_CITATION_KEYS
/** An alias is at most 120 characters; a library id is short. */
const MAX_FILTH_CITATION_KEY_LENGTH = 512
const SPACE_TYPES = ["company", "shared", "personal"] as const

export type FilthSpaceType = (typeof SPACE_TYPES)[number]

export type FilthSearchActivityState =
  | { phase: "authorizing"; backendCount: number }
  | {
      phase: "grounded"
      evidenceCount: number
      fileCount: number
      claimCount: number
      relatedCount: number
      spaceTypes: FilthSpaceType[]
    }

function safeCount(value: unknown, allowZero = true): number | undefined {
  if (!Number.isSafeInteger(value)) return undefined
  const count = value as number
  if (count < (allowZero ? 0 : 1) || count > MAX_ACTIVITY_COUNT) return undefined
  return count
}

function parseFilthSearchState(value: unknown): FilthSearchActivityState | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  const details = value as Record<string, unknown>

  if (details.phase === "authorizing") {
    const backendCount = safeCount(details.backend_count, false)
    return backendCount === undefined ? undefined : { phase: "authorizing", backendCount }
  }

  if (details.phase !== "grounded") return undefined
  const evidenceCount = safeCount(details.evidence_count, false)
  const fileCount = safeCount(details.file_count)
  const claimCount = safeCount(details.claim_count)
  const relatedCount = safeCount(details.related_count)
  const rawSpaceTypes = details.space_types
  if (
    evidenceCount === undefined ||
    fileCount === undefined ||
    claimCount === undefined ||
    relatedCount === undefined ||
    !Array.isArray(rawSpaceTypes)
  ) {
    return undefined
  }

  const allowed = new Set<string>(SPACE_TYPES)
  const spaceTypes = SPACE_TYPES.filter((spaceType) =>
    rawSpaceTypes.some(
      (candidate: unknown) =>
        typeof candidate === "string" && candidate === spaceType && allowed.has(candidate)
    )
  )

  return {
    phase: "grounded",
    evidenceCount,
    fileCount,
    claimCount,
    relatedCount,
    spaceTypes,
  }
}

export function getFilthSearchActivityState(
  messages: ActivityMessage[]
): FilthSearchActivityState | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const state = parseFilthSearchState(messages[index].details?.filth_search)
    if (state) return state
  }
  return undefined
}

/**
 * The FILTH phase Prism follows for this turn. Same parsed shape as
 * `getFilthSearchActivityState`, which still reports the latest parsable
 * activity. A later `authorizing` search must not rewind chips or the step
 * track once this turn has already grounded, so Prism keeps the latest
 * grounded snapshot until that search grounds. A turn that has only
 * authorized still returns that authorizing state. No parsable activity is
 * undefined. Counts come only from a real `filth_search` on this turn.
 */
export function getPrismFilthSearchState(
  messages: ActivityMessage[]
): FilthSearchActivityState | undefined {
  const latest = getFilthSearchActivityState(messages)
  if (!latest || latest.phase === "grounded") return latest

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const state = parseFilthSearchState(messages[index].details?.filth_search)
    if (state?.phase === "grounded") return state
  }
  return latest
}

function parseCitationKeys(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || !value.every((key) => typeof key === "string")) return undefined
  return (value as string[])
    .slice(0, MAX_FILTH_CITATION_KEYS)
    .filter((key) => key.length <= MAX_FILTH_CITATION_KEY_LENGTH && key.indexOf(":") > 0)
}

/**
 * The `[[...]]` citation keys of the FILTH documents this turn's library
 * searches returned (#3261), from every grounded `filth_search` detail: a turn
 * can search more than once. A detail's `citation_keys` count only when they
 * are an array of strings; each detail contributes at most
 * `MAX_FILTH_CITATION_KEYS` and the turn at most `MAX_TURN_FILTH_CITATION_KEYS`.
 * Undefined when no grounded detail carried any, so a caller can tell "no
 * FILTH citation is known" from "nothing could be known". An empty list is
 * no evidence either way (the backend sends one when none of its FILTH
 * results had an alias), so it counts as none.
 *
 * The keys only classify the answer's own citations. They are titles of
 * retrieved documents, cited or not, and must never be rendered.
 */
export function getFilthCitationKeys(messages: ActivityMessage[]): string[] | undefined {
  let found = false
  const keys = new Set<string>()
  for (const message of messages) {
    const details = message.details?.filth_search
    if (!details || typeof details !== "object" || Array.isArray(details)) continue
    const record = details as Record<string, unknown>
    if (record.phase !== "grounded") continue
    const parsed = parseCitationKeys(record.citation_keys)
    // An empty list says nothing: its FILTH results (legacy rows) had no alias.
    if (!parsed?.length) continue
    found = true
    for (const key of parsed) {
      if (keys.size >= MAX_TURN_FILTH_CITATION_KEYS) break
      keys.add(key)
    }
  }
  return found ? Array.from(keys) : undefined
}
